import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Visibility, Edit } from "@mui/icons-material";
import { Box } from "@mui/material";
import { DataGridPro, GridActionsCellItem, } from "@mui/x-data-grid-pro";
import { CError } from "app/common/CError";
import { AddEntityButton } from "app/components/AddEntityButton";
import GridDeleteCellItem from "app/components/GridDeleteCellItem";
import { MoneyFormatted } from "app/components/MoneyFormatted";
import NoRowsOverlay from "app/components/NoRowsOverlay";
import { Loading } from "app/lib/components/Loading";
import { makeNonEmpty } from "core/util/array";
import dayjs from "dayjs";
import { useBatchPaymentsByPartyQuery, useDeleteBatchPaymentMutationMutation, } from "generated/generated-models";
import { useState } from "react";
import { useNavigate } from "react-router";
export const BatchPaymentsPage = ({ allowCreateBatchPayments, }) => {
    const { data, loading, error } = useBatchPaymentsByPartyQuery({
        fetchPolicy: "network-only",
    });
    if (loading) {
        return _jsx(Loading, {});
    }
    if (error) {
        return (_jsx("section", { className: "sub-wrap", children: _jsx(CError, { error: "Could not retrieve batch payments." }) }));
    }
    return (_jsx(BatchPaymentsDataGrid, { data: data, allowCreateBatchPayments: allowCreateBatchPayments }));
};
const BatchPaymentsDataGrid = ({ data, allowCreateBatchPayments, }) => {
    const navigate = useNavigate();
    const [deleteBatchPayment] = useDeleteBatchPaymentMutationMutation();
    const [batchPayments, setBatchPayments] = useState(makeNonEmpty(data?.batchPaymentsByParty) ?? []);
    const handleDeleteBatchPayment = async (id) => {
        await deleteBatchPayment({
            variables: {
                input: { BatchPaymentId: id },
            },
        });
        setBatchPayments((prevBatchPayments) => prevBatchPayments.map((batchPayment) => {
            if (batchPayment.Id === id) {
                return {
                    ...batchPayment,
                    Status: "Deleted",
                };
            }
            return batchPayment;
        }));
    };
    const columns = [
        {
            field: "Description",
            headerName: "Description",
            minWidth: 200,
            flex: 1,
        },
        {
            field: "Total",
            headerName: "Amount",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => (_jsx(MoneyFormatted, { amount: params.row.Total.Amount })),
        },
        {
            field: "PaymentsCount",
            headerName: "Payments",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
        },
        {
            field: "Account",
            headerName: "From Account",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => params.row.Account.Number,
        },
        {
            field: "ScheduledDate",
            headerName: "Scheduled Date",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => dayjs(params.row.ScheduledDate).format("DD/MM/YYYY"),
        },
        {
            field: "Status",
            headerName: "Status",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => params.row.Status.replace(/([A-Z])/g, " $1").trim(),
        },
        {
            field: "actions",
            type: "actions",
            width: 120,
            resizable: false,
            getActions: (params) => {
                const status = params.row.Status;
                const editVisible = ["Pending", "PendingAuthorisation"].includes(status) &&
                    allowCreateBatchPayments;
                const deleteVisible = ["Pending", "PendingAuthorisation", "Authorised"].includes(status) && allowCreateBatchPayments;
                return [
                    _jsx(GridActionsCellItem, { icon: _jsx(Visibility, {}), label: "View", onClick: () => {
                            navigate(`/BatchPayments/${params.row.Id}`);
                        }, color: "inherit", sx: { opacity: "60%" } }, "view"),
                    _jsx(GridActionsCellItem, { icon: _jsx(Edit, {}), label: "Edit", onClick: () => {
                            navigate(`/BatchPayments/Edit/${params.row.Id}`);
                        }, color: "inherit", sx: {
                            opacity: "60%",
                            visibility: !editVisible ? "hidden" : "visible",
                        } }, "edit"),
                    _jsx(GridDeleteCellItem, { sx: {
                            opacity: "60%",
                            visibility: !deleteVisible ? "hidden" : "visible",
                        }, confirmTitle: "Delete Payment", confirmDescription: `Are you sure you want to delete the following batch payment, '${params.row.Description}'?`, confirmNote: "The process cannot be undone", onConfirm: () => handleDeleteBatchPayment(params.row.Id) }, "delete"),
                ];
            },
        },
    ];
    return (_jsxs(_Fragment, { children: [_jsx(Box, { display: "flex", justifyContent: "flex-end", paddingBottom: 2, children: allowCreateBatchPayments && (_jsx(AddEntityButton, { text: "New Batch Payment", href: "/BatchPayments/Create", sx: { marginTop: 0 } })) }), _jsx(DataGridPro, { className: "custom-grid", columns: columns, rows: batchPayments, getRowId: (r) => r.Id, autoHeight: true, pageSizeOptions: [10, 25, 50], pagination: true, initialState: {
                    pagination: { paginationModel: { pageSize: 10 } },
                    pinnedColumns: { right: ["actions"] },
                }, slots: {
                    noRowsOverlay: () => (_jsx(NoRowsOverlay, { children: "There are currently no batch payments for this account." })),
                }, disableColumnSelector: true, sx: {
                    "& .MuiDataGrid-filler": {
                        height: "0 !important",
                    },
                } })] }));
};
